import React, { useState, useRef } from "react";
import "./Card.css";
import { TypePill } from "../../TypePill";
import { CardProps } from "./types";
import ImageSlideshow from "../../ImageSlideshow";
import { CircleButton } from "../../common/CircleButton/circle-button";
import { useAuth } from "../../../contexts/AuthContext";
import { CardActionMenu } from "./CardActionMenu";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { useAppDispatch } from "../../../store/hooks";
import { deleteListing } from "../../../store/slices/listingsSlice";

export const CardDefault: React.FC<CardProps> = ({
  listingId,
  listingUserId,
  imageUrls,
  title,
  type,
  typeColor = "#6b7280",
  onClick,
  selected = false,
  children,
  creators = [],
  subTypeLabel,
  layoutType = "default",
  editing = false,
  onEdit,
}) => {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);

  const canEdit: boolean = !!(listingUserId && user?.userId && listingUserId === user.userId);

  const handleEllipsisClick = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setShowMenu(true);
  };

  const handleCloseMenu = () => {
    setShowMenu(false);
  };

  const handleDeleteClick = () => {
    handleCloseMenu();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    dispatch(deleteListing(listingId));
    setShowDeleteConfirm(false);
  };

  const handleCancelDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setShowDeleteConfirm(false);
  };

  const cardTypeClass = "card--default";

  return (
    <div
      className={`card ${selected ? "selected" : ""} ${cardTypeClass}`}
      onClick={onClick}
    >
      <div
        className={`card__function-overlay card__function-overlay--${editing ? "editing" : "default"}`}
      >
        <div ref={buttonRef}>
          <CircleButton iconKey="ellipsis" onClick={handleEllipsisClick} />
        </div>
      </div>
      <ImageSlideshow images={imageUrls} altText={title} />

      <div className="card-content">
        <div className="card-header">
          <h3 className="card-title">{title}</h3>
          <TypePill label={subTypeLabel} color={typeColor} />
        </div>

        <div className="card-details">{children}</div>
      </div>

      {showMenu && (
        <CardActionMenu
          canEdit={!!canEdit}
          canDelete={!!canEdit}
          position={menuPosition}
          onClose={handleCloseMenu}
          onEdit={() => {
            handleCloseMenu();
            onEdit?.();
          }}
          onDelete={handleDeleteClick}
          onReport={() => {
            handleCloseMenu();
          }}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete Listing"
          message={`Are you sure you want to delete "${title}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          isDestructive={true}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
};
